import { IContract, IPADUCompiled, IPADUCompiledField } from '../definitions/Contract'
import { IContractSchema } from '../definitions/ContractSchema'
import { IFormField, PaduOperation } from '../definitions/FormField'

class PADUEngineClass {
    compile = (contract: IContract, schema: IContractSchema): IPADUCompiled => {
        const fields = this.extractPaduFields(schema)
        const compiled: IPADUCompiledField[] = []

        for (const field of fields) {
            if (!field.padu) continue

            const values = this.resolveValues(contract, field.key)

            if (values.length === 0) {
                compiled.push({
                    key: field.key,
                    paduSetting: field.padu,
                    status: 'none'
                })
                continue
            }

            for (const entry of values) {
                const status = this.evaluatePadu(entry.value, field.padu)

                compiled.push({
                    key: entry.key,
                    paduSetting: field.padu,
                    status
                })
            }
        }
        return {
            contractId: contract.uuid,
            fields: compiled
        }
    }

    extractPaduFields = (schema: IContractSchema): IFormField[] => {
        if (!schema) return []
        const result: IFormField[] = []

        const visit = (fields: IFormField[]) => {
            for (const field of fields) {
                if (field.padu) {
                    result.push(field)
                }

                if (field.objectFields?.length) {
                    visit(field.objectFields)
                }

                if (field.childFields?.length) {
                    visit(field.childFields)
                }
            }
        }

        for (const step of schema.steps) {
            visit(step.fields)
        }

        return result
    }

    resolveValues = (
        obj: any,
        path: string
    ): { key: string, value: any }[] => {
        const parts = path.split('.')

        let current: { value: any, key: string }[] = [
            { value: obj, key: '' }
        ]

        for (const part of parts) {
            const next: { value: any, key: string }[] = []

            for (const item of current) {
                if (item.value == null) continue

                const value = item.value[part]
                const nextKey = item.key ? `${item.key}.${part}` : part

                if (Array.isArray(value)) {
                    for (const v of value) {
                        next.push({
                            value: v,
                            key: nextKey
                        })
                    }
                } else if (value !== undefined) {
                    next.push({
                        value,
                        key: nextKey
                    })
                }
            }

            current = next
        }

        return current
    }

    evaluatePadu = (value: any, padu: IFormField['padu']): IPADUCompiledField['status'] => {
        if (!padu) return 'none'

        if (padu.unacceptable && this.matchRule(value, padu.unacceptable)) {
            return 'unacceptable'
        }

        if (padu.discouraged && this.matchRule(value, padu.discouraged)) {
            return 'discouraged'
        }

        if (padu.acceptable && this.matchRule(value, padu.acceptable)) {
            return 'acceptable'
        }

        if (padu.preferred && this.matchRule(value, padu.preferred)) {
            return 'preferred'
        }

        return 'none'
    }

    matchRule = (
        value: any,
        rule: { operation: PaduOperation, value?: any }
    ): boolean => {
        if (value == null) return false

        const ruleValue = rule.value

        const numericOps = [
            'greater',
            'smaller',
            'greater-equal',
            'smaller-equal',
            'between'
        ]

        if (numericOps.includes(rule.operation)) {
            const v = Number(value)

            switch (rule.operation) {
                case 'equal':
                    return v === ruleValue

                case 'greater':
                    return v > ruleValue

                case 'smaller':
                    return v < ruleValue

                case 'greater-equal':
                    return v >= ruleValue

                case 'smaller-equal':
                    return v <= ruleValue

                case 'between': {
                    if (!ruleValue || typeof ruleValue !== 'object') return false

                    const { from, to } = ruleValue

                    if (from !== undefined && v < from) return false
                    if (to !== undefined && v > to) return false

                    return true
                }

                default:
                    return false
            }
        }

        const v = String(value).toLowerCase()

        const ruleValues = Array.isArray(ruleValue) ? ruleValue : [ruleValue]

        for (const rv of ruleValues) {
            const r = String(rv ?? '').toLowerCase()

            switch (rule.operation) {
                case 'contains':
                    if (v.includes(r)) return true
                    break

                case 'not-contains':
                    if (!v.includes(r)) return true
                    break

                case 'starts-with':
                    if (v.startsWith(r)) return true
                    break

                case 'ends-with':
                    if (v.endsWith(r)) return true
                    break

                case 'equal':
                    if (v === r) return true
                    break
            }
        }
        return false
    }
}

const PADUEngine = new PADUEngineClass()
export default PADUEngine
